import { useMutation } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { userService, RegisterUserData } from '../services/api/user.service';
import { IUser } from '../@shared/interfaces';
import { useAuthStore } from '../stores/auth.store';

interface UseUserRegistrationReturn {
  registerUser: (
    data: Omit<RegisterUserData, 'auth0Uid' | 'email' | 'photoUrl' | 'provider'>
  ) => Promise<IUser>;
  checkUserExists: (email: string, auth0Uid: string) => Promise<boolean>;
  isLoading: boolean;
  error: Error | null;
}

export const useUserRegistration = (): UseUserRegistrationReturn => {
  const { user: auth0User } = useAuth0();

  const checkUserExists = async (
    email: string,
    auth0Uid: string
  ): Promise<boolean> => {
    try {
      const response = await userService.checkUser(email, auth0Uid);
      return response.success && response.existingUser;
    } catch (error) {
      console.error('Error checking user existence:', error);
      return false;
    }
  };

  const registerMutation = useMutation({
    mutationFn: async (
      data: Omit<
        RegisterUserData,
        'auth0Uid' | 'email' | 'photoUrl' | 'provider'
      >
    ): Promise<IUser> => {
      if (!auth0User?.sub || !auth0User?.email) {
        throw new Error('Auth0 user data is incomplete');
      }

      // First check if user exists
      const userExists = await checkUserExists(auth0User.email, auth0User.sub);
      if (userExists) {
        throw new Error('User already exists');
      }

      const registrationData: RegisterUserData = {
        ...data,
        auth0Uid: auth0User.sub,
        email: auth0User.email,
        photoUrl: auth0User.picture,
        provider: auth0User.sub.split('|')[0],
      };

      try {
        const response = await userService.registerUser(registrationData);
        if (!response.success) {
          // If registration failed, check again if user exists (race condition)
          const userExistsNow = await checkUserExists(
            auth0User.email,
            auth0User.sub
          );
          if (userExistsNow) {
            // If user exists now, it means another registration succeeded
            throw new Error('User already exists');
          }
          throw new Error(response.message || 'Failed to register user');
        }
        return response.user;
      } catch (error) {
        // If we get a conflict error, check if user exists
        if (
          error instanceof Error &&
          error.message.includes('User already exists')
        ) {
          const userExistsNow = await checkUserExists(
            auth0User.email,
            auth0User.sub
          );
          if (userExistsNow) {
            // If user exists now, it means another registration succeeded
            throw new Error('User already exists');
          }
        }
        throw error;
      }
    },
    onSuccess: async () => {
      // BUG FIX: Call fetchProfile directly from store to avoid race condition
      await useAuthStore.getState().fetchProfile();
    },
  });

  return {
    registerUser: registerMutation.mutateAsync,
    checkUserExists,
    isLoading: registerMutation.isPending,
    error: registerMutation.error as Error | null,
  };
};
